import { Badge, Button, Card, Empty, List, Segmented, Space, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import useDocumentTitle from '../../lib/useDocumentTitle'
import { PageHeader } from '@ant-design/pro-layout'
import { useFirestore, useUser } from 'reactfire'
import { collection, orderBy, query, where } from 'firebase/firestore'
import { useBreakpointContext } from '../../lib/useBreakpointContext'
import {
  MarkAllReadButton,
  NotificationByType,
} from '../../components/Notifications/Notifications'
import { useUnreadNotificationCount } from '../../lib/useUnreadNotificationCount'
import { usePaginatedCollection } from '../../lib/usePaginatedCollection'
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons'
import { useState } from 'react'
import EmptyImage from '../../resources/img/man-working-on-laptop.svg'

export function NotificationsPage() {
  const { t } = useTranslation()
  useDocumentTitle(t('frontend:myAccount.notificationsPage.documentTitle'))

  const [filter, setFilter] = useState('all')

  // usePaginatedCollection fixes its query on mount, so the content is keyed
  // by filter to reinitialize pagination when the filter changes.
  return (
    <NotificationsPageContent
      key={filter}
      filter={filter}
      onFilterChange={setFilter}
    />
  )
}

function NotificationsPageContent({ filter, onFilterChange }) {
  const { t } = useTranslation()
  const user = useUser()
  const firestore = useFirestore()

  const filterQueries = {
    all: query(
      collection(firestore, 'notifications'),
      where('userUids', 'array-contains', user.data.uid),
      orderBy('createdAt', 'desc')
    ),
    unread: query(
      collection(firestore, 'notifications'),
      where('userUids', 'array-contains', user.data.uid),
      where('dismissedAt', '==', null),
      orderBy('createdAt', 'desc')
    ),
    // The inequality field must be the first ordering, so the read view is
    // sorted by dismissal time (most recently read first).
    read: query(
      collection(firestore, 'notifications'),
      where('userUids', 'array-contains', user.data.uid),
      where('dismissedAt', '!=', null),
      orderBy('dismissedAt', 'desc')
    ),
  }

  const paginatedNotifications = usePaginatedCollection({
    query: filterQueries[filter],
    limit: 10,
  })

  const notifications = paginatedNotifications.data.docs.map((doc) => ({
    ...doc.data(),
    uid: doc.id,
  }))

  const breakpoint = useBreakpointContext()
  const { token } = theme.useToken()

  const unreadNotificationCount = useUnreadNotificationCount()
  const dismissableUnreadCount = useUnreadNotificationCount({
    dismissableOnly: true,
  })

  return (
    <>
      <PageHeader
        style={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
        breadcrumb={{
          items: [
            {
              key: 'my-account',
              title: t('frontend:myAccount.breadcrumb.myAccount'),
            },
            {
              key: 'notifications',
              title: t('frontend:myAccount.breadcrumb.notifications'),
            },
          ],
        }}
        title={t('frontend:myAccount.notificationsPage.title')}
        tags={<Badge count={unreadNotificationCount} />}
        extra={
          <Space wrap>
            <Segmented
              value={filter}
              onChange={onFilterChange}
              options={[
                { value: 'all', label: t('frontend:notifications.filterAll') },
                {
                  value: 'unread',
                  label: t('frontend:notifications.filterUnread'),
                },
                {
                  value: 'read',
                  label: t('frontend:notifications.filterRead'),
                },
              ]}
            />
            <MarkAllReadButton unreadCount={dismissableUnreadCount} />
            <Space.Compact>
              <Button
                type="text"
                disabled={paginatedNotifications.prevDisabled}
                onClick={() => paginatedNotifications.prev()}
              >
                <ArrowLeftOutlined />
              </Button>
              <Button
                type="text"
                disabled={paginatedNotifications.nextDisabled}
                onClick={() => paginatedNotifications.next()}
              >
                <ArrowRightOutlined />
              </Button>
            </Space.Compact>
          </Space>
        }
      />
      {notifications.length === 0 ? (
        <Card bordered={false}>
          <Empty
            image={EmptyImage}
            imageStyle={{ height: 220 }}
            description={t('frontend:myAccount.notificationsPage.empty')}
          />
        </Card>
      ) : (
        <List
          dataSource={notifications}
          pagination={false}
          split={false}
          grid={{ gutter: 0, column: 1 }}
          itemLayout={breakpoint.xs ? 'vertical' : 'horizontal'}
          renderItem={(notification) => (
            <List.Item key={notification.id}>
              <Card
                bordered={false}
                size="small"
                styles={{
                  body: {
                    paddingLeft: token.paddingLG,
                    paddingRight: token.paddingLG,
                  },
                }}
              >
                <NotificationByType notification={notification} />
              </Card>
            </List.Item>
          )}
        />
      )}
    </>
  )
}
